import { isLoggedIn, LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { SyncRequestClient } from "../stores/requestClient";
import { type DrawingID, ServiceErrorCode } from "../types";
import { buildTypePredicateFilter } from "../utils/typeUtils";
import type { Spherical, Vector2, Vector3 } from "./types";
import type { UserID } from "./useAccount";
import { useWebExport } from "./useWebExport";

const CommentsQueryKey = ["commentsQueryKey"] as const;

export const CommentID = z.string().brand("CommentID");
export type CommentID = z.infer<typeof CommentID>;

export type ModelCommentV2 = {
  commentPosition: Vector3;
  commentNormal: Vector3;
  cameraTarget: Vector3;
  cameraOrbit: Spherical;
  fieldOfView: number;
};

export type DrawingCommentV2 = {
  drawingId: DrawingID;
  commentPosition: Vector2;
  zoomLevel: string;
};

export type Reply = {
  commentId: CommentID;
  createdAt: string;
  userId: UserID;
  message: string;
};

export interface CommentBase {
  commentId: CommentID;
  createdAt: string;
  createdByUserId: UserID;
  message: string;
  replies?: Reply[];
}

export interface ModelComment extends CommentBase {
  model: ModelCommentV2;
}

export interface DrawingComment extends CommentBase {
  drawing: DrawingCommentV2;
}

export type Comment = ModelComment | DrawingComment;

export type User = {
  userId: UserID;
  email: string;
  name?: string;
};

export type CommentsResponseData = {
  comments: Comment[];
  users: User[];
};

export const isModelComment = buildTypePredicateFilter<Comment, ModelComment>("model");
export const isDrawingComment = buildTypePredicateFilter<Comment, DrawingComment>("drawing");

export const useComments = () => {
  const { viewLinkId } = useParams();
  const { data: webExportData } = useWebExport();

  return useQuery({
    queryKey: [...CommentsQueryKey, viewLinkId],
    queryFn: async ({ signal }) => {
      try {
        const bodyRequest = {
          method: "GET",
          url: `/web/comments?v=${viewLinkId}`,
          abortSignal: signal,
        };
        const response = !isLoggedIn()
          ? await SyncRequestClient.publicRequest(bodyRequest)
          : await SyncRequestClient.authenticatedRequest(bodyRequest);
        return response.json<CommentsResponseData>();
      } catch (error) {
        LogSentryException({ messagePrefix: "Failed to fetch comments", error });
        throw error;
      }
    },
    select: (data) => {
      const modelComments: ModelComment[] = data.comments.filter(isModelComment);
      const drawingComments: DrawingComment[] = data.comments.filter(isDrawingComment);

      const users: { [key: UserID]: string } = Object.fromEntries(
        data.users.map((user) => [user.userId, user.name ?? user.email])
      );

      return { ...data, modelComments, drawingComments, users };
    },
    enabled: Boolean(viewLinkId) && Boolean(webExportData?.isCommentingEnabled),
  });
};

interface CommentsRequestPayloadBase {
  message: string;
}

interface ModelCommentRequestPayload extends CommentsRequestPayloadBase {
  model: ModelCommentV2;
}

interface DrawingCommentRequestPayload extends CommentsRequestPayloadBase {
  drawing: DrawingCommentV2;
}

export type CommentsRequestPayload = ModelCommentRequestPayload | DrawingCommentRequestPayload;

export const useMutateComments = () => {
  const { viewLinkId } = useParams();
  const { data: webExportData } = useWebExport();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ body }: { body: CommentsRequestPayload }) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/comments`,
        body: {
          ...body,
          projectViewLinkId: viewLinkId,
          webExportId: webExportData?.webExport.webExportId,
        },
      });
      return response.json<Comment>();
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [...CommentsQueryKey, viewLinkId],
        (oldData: CommentsResponseData) => {
          return {
            ...oldData,
            comments: [...oldData.comments, data],
          };
        }
      );
      // Ensure the correct server ordering of the comments
      queryClient.invalidateQueries({ queryKey: [...CommentsQueryKey, viewLinkId] });
    },
    onError(error) {
      LogSentryException({ messagePrefix: "Failed to add comment", error });
    },
  });
};

export const useMutateDeleteComment = () => {
  const { viewLinkId } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ commentId }: { commentId: CommentID }) => {
      try {
        await SyncRequestClient.authenticatedRequest({
          method: "DELETE",
          url: `/web/comments/${commentId}`,
        });
      } catch (error) {
        if (error instanceof ServiceError) {
          if (error.hasCode(ServiceErrorCode.CommentNotFound)) {
            return commentId;
          }
        }
        throw error;
      }

      return commentId;
    },
    onSuccess(commentId) {
      queryClient.setQueryData(
        [...CommentsQueryKey, viewLinkId],
        (oldData: CommentsResponseData) => {
          const newComments = oldData.comments.filter((comment) => comment.commentId !== commentId);

          return {
            ...oldData,
            comments: [...newComments],
          };
        }
      );
    },
    onError(error) {
      LogSentryException({ messagePrefix: "Failed to delete comment", error });
      toast.error("Failed to delete comment. You may try again later.");
    },
  });
};
